import React from 'react';
import { Image, Pressable, StyleSheet, Text, View } from 'react-native';

import { colors, radii, spacing, typography } from '../../../core/theme/tokens';
import type { ImportLocalImage } from '../../../types/import';
import { isPdfMime } from '../utils/imageHelpers';

type Props = {
  images: ImportLocalImage[];
  onRemove: (id: string) => void;
  disabled?: boolean;
};

export function ImportImageList({ images, onRemove, disabled }: Props) {
  if (!images.length) {
    return null;
  }

  return (
    <View style={styles.list} accessibilityRole="list">
      {images.map((image, index) => {
        const pdf = isPdfMime(image.mimeType);
        return (
          <View key={image.id} style={styles.row}>
            <View style={styles.thumbWrap}>
              {pdf ? (
                <View style={[styles.thumb, styles.pdfThumb]}>
                  <Text style={styles.pdfLabel}>PDF</Text>
                </View>
              ) : image.previewUri || image.dataUrl ? (
                <Image
                  source={{ uri: image.previewUri || image.dataUrl }}
                  style={styles.thumb}
                  accessibilityIgnoresInvertColors
                />
              ) : (
                <View style={[styles.thumb, styles.thumbFallback]} />
              )}
            </View>
            <View style={styles.meta}>
              <Text style={styles.name} numberOfLines={1}>
                {image.name}
              </Text>
              <Text style={styles.caption}>
                {pdf ? `Statement ${index + 1}` : `Screenshot ${index + 1}`}
              </Text>
            </View>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel={`Remove ${image.name}`}
              disabled={disabled}
              onPress={() => onRemove(image.id)}
              hitSlop={8}
              style={({ pressed }) => [
                styles.remove,
                pressed && styles.removePressed,
                disabled && styles.disabled,
              ]}
            >
              <Text style={styles.removeText}>Remove</Text>
            </Pressable>
          </View>
        );
      })}
    </View>
  );
}

const styles = StyleSheet.create({
  list: {
    gap: spacing.sm,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingVertical: spacing.xs,
    paddingHorizontal: spacing.sm,
    borderRadius: radii.md,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: colors.glassBorder,
    backgroundColor: 'rgba(255,255,255,0.03)',
  },
  thumbWrap: {
    width: 44,
    height: 44,
    borderRadius: radii.sm,
    overflow: 'hidden',
    backgroundColor: colors.surface,
  },
  thumb: {
    width: 44,
    height: 44,
    alignItems: 'center',
    justifyContent: 'center',
  },
  pdfThumb: {
    backgroundColor: 'rgba(61, 214, 164, 0.12)',
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: 'rgba(61, 214, 164, 0.35)',
  },
  pdfLabel: {
    ...typography.label,
    color: colors.accent,
    fontWeight: '700',
    letterSpacing: 0.5,
  },
  thumbFallback: {
    backgroundColor: colors.border,
  },
  meta: {
    flex: 1,
    minWidth: 0,
  },
  name: {
    ...typography.caption,
    color: colors.text,
    fontWeight: '600',
  },
  caption: {
    ...typography.label,
    color: colors.textMuted,
    marginTop: 2,
    textTransform: 'uppercase',
  },
  remove: {
    minHeight: 44,
    justifyContent: 'center',
    paddingHorizontal: spacing.sm,
  },
  removePressed: {
    opacity: 0.7,
  },
  removeText: {
    ...typography.caption,
    color: colors.danger,
    fontWeight: '600',
  },
  disabled: {
    opacity: 0.45,
  },
});
